// src/pages/BlogPage/BlogPage.tsx

import { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArticleCard } from '../../components/molecules/ArticleCard/ArticleCard'
import { useAuth } from '../../hooks/useAuth'
import * as articleService from '../../services/articleService'
import type { Article } from '../../types/article'
import styles from './BlogPage.module.css'

// PAGINATION DÉSACTIVÉE — décommenter pour réactiver
// const PAGE_SIZE = 6

/* ─────────────────────────────────────────────────
   Nombre de skeletons affichés pendant le chargement.
   Sans pagination, on utilise une valeur fixe estimée.
───────────────────────────────────────────────── */
const SKELETON_COUNT = 6

/* ─────────────────────────────────────────────────
   Icônes SVG inline
───────────────────────────────────────────────── */

// PAGINATION DÉSACTIVÉE — icônes chevron inutilisées pour l'instant
// const IconChevronLeft = () => (...)
// const IconChevronRight = () => (...)

const IconSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.searchIcon}>
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
)

const IconX = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.clearIcon}>
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

/* ─────────────────────────────────────────────────
   Skeleton card — affiché pendant le chargement
───────────────────────────────────────────────── */

function SkeletonCard() {
  return (
    <div className={styles.skeleton} aria-hidden="true">
      <div className={styles.skeletonHeader}>
        <div className={styles.skeletonBadge} />
        <div className={styles.skeletonDate} />
      </div>
      <div className={styles.skeletonTitle} />
      <div className={styles.skeletonLine} />
      <div className={styles.skeletonLine} style={{ width: '80%' }} />
      <div className={styles.skeletonLine} style={{ width: '60%' }} />
      <div className={styles.skeletonTags}>
        <div className={styles.skeletonTag} />
        <div className={styles.skeletonTag} />
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────────────
   Dropdown multiselect
───────────────────────────────────────────────── */

interface MultiSelectDropdownProps {
  label: string
  options: string[]
  selected: string[]
  onToggle: (value: string) => void
  formatOption?: (value: string) => string
}

function MultiSelectDropdown({ label, options, selected, onToggle, formatOption }: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={containerRef} className={styles.dropdown}>
      <button
        type="button"
        className={`${styles.dropdownTrigger} ${selected.length > 0 ? styles.dropdownTriggerActive : ''}`}
        onClick={() => setIsOpen((o) => !o)}
        aria-expanded={isOpen}
      >
        <span className={styles.dropdownTriggerLabel}>{label}</span>
        {selected.length > 0 && (
          <span className={styles.dropdownBadge}>{selected.length}</span>
        )}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={`${styles.dropdownChevron} ${isOpen ? styles.dropdownChevronOpen : ''}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.dropdownMenu} role="listbox" aria-multiselectable="true">
          {options.map((option) => {
            const isChecked = selected.includes(option)
            return (
              <label
                key={option}
                className={`${styles.dropdownItem} ${isChecked ? styles.dropdownItemChecked : ''}`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => onToggle(option)}
                  className={styles.dropdownCheckbox}
                />
                <span className={styles.dropdownItemText}>
                  {formatOption ? formatOption(option) : option}
                </span>
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}

/* ─────────────────────────────────────────────────
   Composant principal
───────────────────────────────────────────────── */

export function BlogPage() {
  /* ── État de recherche ── */
  const [searchQuery, setSearchQuery] = useState('')

  /* ── État des filtres sidebar ── */
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [dateFilter, setDateFilter] = useState<string>('')
  const [showPublishedOnly, setShowPublishedOnly] = useState(false)

  /* ── État des articles ── */
  const [articles, setArticles] = useState<Article[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // création d'article (authentifié uniquement)
  const { user, token } = useAuth()

  // Vérifier si l'utilisateur est admin
  const isAdmin = user?.roles?.includes('Admin') ?? false

  /* ── Chargement des articles ── */
  const loadArticles = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      let data: Article[]
      if (isAdmin && token) {
        // Admin voit tous les articles
        data = await articleService.fetchAllArticles(token)
      } else {
        // Utilisateur non connecté ou non-admin voit seulement les articles publiés
        data = await articleService.fetchArticles()
      }
      setArticles(data)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [isAdmin, token])

  useEffect(() => {
    loadArticles()
  }, [loadArticles])

  const refetch = useCallback(() => {
    loadArticles()
  }, [loadArticles])

  /* ── Filtres et tri côté client ──────────────────────────────────────
   * Applique recherche par titre, filtres par catégories/tags/date,
   * et tri par date décroissante (plus récent en premier).
   ─────────────────────────────────────────────────────────────── */
  const filteredAndSortedArticles = useMemo(() => {
    let result = articles

    // Recherche par titre
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim()
      result = result.filter((article) =>
        article.title.toLowerCase().includes(query)
      )
    }

    // Filtre par catégories sélectionnées
    if (selectedCategories.length > 0) {
      result = result.filter((article) =>
        selectedCategories.some((cat) => article.categories.includes(cat))
      )
    }

    // Filtre par tags sélectionnés
    if (selectedTags.length > 0) {
      result = result.filter((article) =>
        selectedTags.some((tag) => article.tags.includes(tag))
      )
    }

    // Filtre par date
    if (dateFilter) {
      const now = new Date()
      const filterDate = new Date()

      switch (dateFilter) {
        case 'week':
          filterDate.setDate(now.getDate() - 7)
          break
        case 'month':
          filterDate.setMonth(now.getMonth() - 1)
          break
        case 'year':
          filterDate.setFullYear(now.getFullYear() - 1)
          break
        default:
          break
      }

      if (dateFilter !== 'all') {
        result = result.filter((article) =>
          new Date(article.publishedAt) >= filterDate
        )
      }
    }

    // Filtre "Publié uniquement" (admin)
    if (showPublishedOnly) {
      result = result.filter((article) => article.isPublished)
    }

    // Tri par date décroissante (plus récent en premier)
    result.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())

    return result
  }, [articles, searchQuery, selectedCategories, selectedTags, dateFilter, showPublishedOnly])

  /* Collecte toutes les catégories et tags uniques de TOUS les articles
   * (pas seulement les filtrés) pour afficher tous les choix disponibles. */
  const allCategories = useMemo(
    () => Array.from(new Set(articles.flatMap((a) => a.categories))).filter(Boolean).sort(),
    [articles]
  )
  const allTags = useMemo(
    () => Array.from(new Set(articles.flatMap((a) => a.tags))).filter(Boolean).sort(),
    [articles]
  )

  /* ── Handlers pour les filtres ── */
  const handleCategoryToggle = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    )
  }

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag)
        ? prev.filter((t) => t !== tag)
        : [...prev, tag]
    )
  }

  const handleDateFilterChange = (filter: string) => {
    setDateFilter(filter)
  }

  const clearAllFilters = () => {
    setSearchQuery('')
    setSelectedCategories([])
    setSelectedTags([])
    setDateFilter('')
    setShowPublishedOnly(false)
  }

  const hasActiveFilters = !!(
    searchQuery.trim() ||
    selectedCategories.length > 0 ||
    selectedTags.length > 0 ||
    dateFilter ||
    showPublishedOnly
  )

  /* ── Render ── */
  return (
    <div className={styles.page}>
      <Helmet>
        <title>Steven YAMBOS — Blog</title>
        <meta name="description" content="Articles techniques de Steven YAMBOS sur C# .NET, ASP.NET Core, microservices, Clean Architecture, PostgreSQL, Docker et plus encore." />
        <meta property="og:title" content="Steven YAMBOS — Blog" />
        <meta property="og:description" content="Articles techniques sur C# .NET, ASP.NET Core, microservices, Clean Architecture et DevOps." />
        <meta property="og:url" content="https://stevenyambos.fr/blog" />
      </Helmet>

      {/* Décoration de fond */}
      <div className={styles.bgGrid} aria-hidden="true" />
      <div className={styles.bgOrb}  aria-hidden="true" />

      {/* ── En-tête ── */}
      <div className={styles.header}>
        <div className={styles.breadcrumb}>
          <Link to="/" className={styles.breadcrumbLink}>Accueil</Link>
          <span className={styles.breadcrumbSep} aria-hidden="true">/</span>
          <span>Blog</span>
        </div>
        <h1 className={styles.title}>Blog</h1>
        <div className={styles.titleBar} aria-hidden="true" />
        <p className={styles.subtitle}>
          Vous trouverez ici mes derniers articles sur différents sujets qui me passionnent, que ce soit des retours d'expérience, des explorations techniques, ou simplement mes réflexions du moment. N'hésitez pas à me contacter pour en discuter je suis très réactif et ouvert à la discussion !
        </p>
      </div>

      {/* ── Barre de recherche ── */}
      <div className={styles.searchBar}>
        <div className={styles.searchInputWrapper}>
          <IconSearch />
          <input
            type="text"
            placeholder="Rechercher par titre..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
            aria-label="Rechercher dans les articles"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className={styles.clearSearchBtn}
              aria-label="Effacer la recherche"
            >
              <IconX />
            </button>
          )}
        </div>
      </div>

      {/* ── Layout principal : sidebar + contenu ── */}
      <div className={styles.mainLayout}>
        {/* Sidebar avec filtres */}
        <aside className={styles.sidebar}>
          <div className={styles.sidebarContent}>
            <h2 className={styles.sidebarTitle}>Filtres</h2>

            {/* Filtre "Publié uniquement" — admin uniquement */}
            {isAdmin && (
              <div className={styles.filterSection}>
                <h3 className={styles.filterSectionTitle}>Statut</h3>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={showPublishedOnly}
                    onChange={(e) => setShowPublishedOnly(e.target.checked)}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>Publiés uniquement</span>
                </label>
              </div>
            )}

            {/* Filtre par date */}
            <div className={styles.filterSection}>
              <h3 className={styles.filterSectionTitle}>Date</h3>
              <div className={styles.dateFilters}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="radio"
                    name="dateFilter"
                    value=""
                    checked={dateFilter === ''}
                    onChange={() => handleDateFilterChange('')}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>Toutes les dates</span>
                </label>
                <label className={styles.checkboxLabel}>
                  <input
                    type="radio"
                    name="dateFilter"
                    value="week"
                    checked={dateFilter === 'week'}
                    onChange={() => handleDateFilterChange('week')}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>Cette semaine</span>
                </label>
                <label className={styles.checkboxLabel}>
                  <input
                    type="radio"
                    name="dateFilter"
                    value="month"
                    checked={dateFilter === 'month'}
                    onChange={() => handleDateFilterChange('month')}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>Ce mois</span>
                </label>
                <label className={styles.checkboxLabel}>
                  <input
                    type="radio"
                    name="dateFilter"
                    value="year"
                    checked={dateFilter === 'year'}
                    onChange={() => handleDateFilterChange('year')}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>Cette année</span>
                </label>
              </div>
            </div>

            {/* Filtre par catégories */}
            {allCategories.length > 0 && (
              <div className={styles.filterSection}>
                <h3 className={styles.filterSectionTitle}>Catégories</h3>
                <MultiSelectDropdown
                  label="Sélectionner"
                  options={allCategories}
                  selected={selectedCategories}
                  onToggle={handleCategoryToggle}
                />
              </div>
            )}

            {/* Filtre par tags */}
            {allTags.length > 0 && (
              <div className={styles.filterSection}>
                <h3 className={styles.filterSectionTitle}>Tags</h3>
                <MultiSelectDropdown
                  label="Sélectionner"
                  options={allTags}
                  selected={selectedTags}
                  onToggle={handleTagToggle}
                  formatOption={(tag) => `#${tag}`}
                />
              </div>
            )}

            {/* Bouton effacer tous les filtres */}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearAllFilters}
                className={styles.clearFiltersBtn}
              >
                <IconX />
                Effacer tous les filtres
              </button>
            )}
          </div>
        </aside>

        {/* Contenu principal */}
        <main className={styles.mainContent}>
          {/* ── État d'erreur ── */}
          {error && (
            <div className={styles.errorState} role="alert">
              <p>{error}</p>
              <button type="button" className={styles.retryBtn} onClick={refetch}>
                Réessayer
              </button>
            </div>
          )}

          {/* ── Grille articles ── */}
          {!error && (
            <>
              <ul
                className={styles.grid}
                role="list"
                aria-label="Liste des articles"
                aria-busy={loading}
              >
                {loading
                  ? /* Skeletons pendant le chargement */
                    Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                      <li key={i}><SkeletonCard /></li>
                    ))
                  : filteredAndSortedArticles.length > 0
                    ? filteredAndSortedArticles.map((article) => (
                        <li key={article.id}>
                          <ArticleCard article={article} showPublishBadge={!!user} />
                        </li>
                      ))
                    : null
                }
              </ul>

              {/* État vide (hors chargement) */}
              {!loading && filteredAndSortedArticles.length === 0 && (
                <div className={styles.emptyState}>
                  <p>Aucun article trouvé{hasActiveFilters ? ' pour ces filtres' : ''}.</p>
                  {hasActiveFilters && (
                    <button type="button" className={styles.retryBtn} onClick={clearAllFilters}>
                      Voir tous les articles
                    </button>
                  )}
                </div>
              )}

              {/*
                PAGINATION DÉSACTIVÉE — décommenter pour réactiver
                {!loading && totalPages > 1 && (
                  <nav className={styles.pagination} aria-label="Pagination des articles">
                    ...
                  </nav>
                )}
              */}
            </>
          )}
        </main>
      </div>
    </div>
  )
}